import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import InventorySearchInput from '../../../components/base/InventorySearchInput';
import { useVehicles } from '../../../hooks/useVehicles';

const TRUST_ITEMS = [
  { icon: 'ri-money-dollar-circle-line', label: 'Avg. $2,400 Saved',  mobileLabel: '$2,400 Saved',  sub: 'per client, on average', priority: 1 },
  { icon: 'ri-flashlight-line', label: 'Replies < 1 Hour',   mobileLabel: '< 1 Hr Reply',  sub: 'guaranteed', priority: 3 },
  { icon: 'ri-shield-check-line', label: 'One Flat Fee',       mobileLabel: 'Flat Fee',       sub: 'no hidden costs', priority: 2 },
  { icon: 'ri-footprint-line', label: '$100 Donated',       mobileLabel: '$100 Donated',   sub: 'per vehicle sold', priority: 4 },
];

const CURRENT_YEAR = new Date().getFullYear();
const MAX_PRICES = ['Any Price', 'Under $10,000', 'Under $15,000', 'Under $20,000', 'Under $25,000', 'Under $30,000', 'Under $40,000'];

interface SelectDropdownProps {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
  icon: string;
  compact?: boolean;
}

function SelectDropdown({ label, value, options, onChange, icon, compact = false }: SelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const isDefault = value === options[0];
  const height = compact ? '42px' : '56px';

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 cursor-pointer transition-colors"
        style={{
          height,
          background: 'transparent',
          border: 'none',
          outline: 'none',
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
      >
        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0" aria-hidden="true">
          <i className={icon} style={{ fontSize: '0.875rem', color: '#6B7280' }}></i>
        </span>
        <span className="flex-1 text-left truncate" style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: compact ? '0.8rem' : '0.875rem',
          fontWeight: isDefault ? 400 : 600,
          color: isDefault ? '#4B5563' : '#1A2332',
        }}>
          {value}
        </span>
        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-xs" aria-hidden="true" style={{ color: '#6B7280' }}>
          {open ? '▲' : '▼'}
        </span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            minWidth: '100%',
            backgroundColor: '#fff',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            maxHeight: '220px',
            overflowY: 'auto',
            zIndex: 9999,
          }}
          role="listbox"
        >
          {options.map(opt => (
            <button
              key={opt}
              type="button"
              role="option"
              aria-selected={value === opt}
              onClick={() => { onChange(opt); setOpen(false); }}
              className="w-full text-left px-4 py-2.5 cursor-pointer transition-colors"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '0.85rem',
                color: value === opt ? '#1A2332' : '#374151',
                fontWeight: value === opt ? 600 : 400,
                background: value === opt ? '#FEF9F0' : 'transparent',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#F9FAFB'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = value === opt ? '#FEF9F0' : 'transparent'; }}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Hero() {
  const navigate = useNavigate();
  const { vehicles } = useVehicles();

  const [keyword, setKeyword] = useState('');
  const [make, setMake] = useState('Any Make');
  const [year, setYear] = useState('Any Year');
  const [maxPrice, setMaxPrice] = useState('Any Price');
  const [activeTab, setActiveTab] = useState<'search' | 'broker'>('search');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Derive unique makes from actual inventory, sorted alphabetically
  const MAKES = useMemo(() => {
    const uniqueMakes = Array.from(
      new Set(vehicles.map(v => v.make).filter((m): m is string => Boolean(m)))
    ).sort((a, b) => a.localeCompare(b));
    return ['Any Make', ...uniqueMakes];
  }, [vehicles]);

  // Derive unique years from actual inventory, newest first
  const YEARS = useMemo(() => {
    const uniqueYears = Array.from(
      new Set(vehicles.map(v => v.year).filter((y): y is number => typeof y === 'number' && y > 1900))
    ).sort((a, b) => b - a);
    return ['Any Year', ...uniqueYears.map(String)];
  }, [vehicles]);

  // Count vehicles per make for the filter badge
  const makeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    vehicles.forEach(v => {
      if (v.make) counts[v.make] = (counts[v.make] ?? 0) + 1;
    });
    return counts;
  }, [vehicles]);

  // Live preview count — updates as user picks filters inside the sheet
  const previewMatchCount = useMemo(() => {
    return vehicles.filter(v => {
      if (make !== 'Any Make' && v.make !== make) return false;
      if (year !== 'Any Year' && String(v.year) !== year) return false;
      if (maxPrice !== 'Any Price') {
        const cap = Number(maxPrice.replace(/[^0-9]/g, ''));
        if (cap && v.price > cap) return false;
      }
      return true;
    }).length;
  }, [vehicles, make, year, maxPrice]);

  // Count active filters for badge
  const activeFilterCount = [
    make !== 'Any Make',
    year !== 'Any Year',
    maxPrice !== 'Any Price',
  ].filter(Boolean).length;

  function handleSearch() {
    const params = new URLSearchParams();
    if (keyword.trim()) params.set('search', keyword.trim());
    if (make !== 'Any Make') params.set('make', make);
    if (year !== 'Any Year') params.set('year', year);
    if (maxPrice !== 'Any Price') {
      const num = maxPrice.replace(/[^0-9]/g, '');
      if (num) params.set('maxPrice', num);
    }
    setMobileFiltersOpen(false);
    navigate(`/inventory${params.toString() ? `?${params.toString()}` : ''}`);
  }

  function clearFilters() {
    setMake('Any Make');
    setYear('Any Year');
    setMaxPrice('Any Price');
  }

  return (
    <section
      className="relative w-full"
      style={{
        marginTop: 'calc(-1 * var(--nav-height))',
        minHeight: 'clamp(420px, 55vh, 600px)',
      }}
      aria-label="Rockhound Auto — Used Cars Warrenton Oregon"
      itemScope
      itemType="https://schema.org/AutoDealer"
    >
      <meta itemProp="name" content="Rockhound Auto Sales" />
      <meta itemProp="address" content="1425 SE 9th St, Warrenton, OR 97146" />
      <meta itemProp="telephone" content="+18554686348" />
      <meta itemProp="url" content="https://rockhoundauto.com" />

      {/* ── Dark placeholder shown instantly while LCP image loads ── */}
      <div className="hero-img-placeholder" aria-hidden="true" />

      {/* Full-bleed background image */}
      <picture className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
        <source
          media="(max-width: 639px)"
          type="image/webp"
          srcSet="https://public.readdy.ai/ai/img_res/8733da202b0e0e4d8a434deca202dc34.jpg"
          width={640}
          height={480}
        />
        <source
          media="(min-width: 640px) and (max-width: 1023px)"
          type="image/webp"
          srcSet="https://readdy.ai/api/search-image?query=dramatic%20cinematic%20wide%20shot%20of%20a%20rugged%20Pacific%20Northwest%20coastal%20highway%20at%20golden%20hour%2C%20a%20sleek%20dark%20SUV%20parked%20on%20the%20cliff%20edge%20overlooking%20the%20Oregon%20coast%20ocean%2C%20moody%20amber%20and%20deep%20teal%20sky%2C%20misty%20sea%20spray%2C%20cinematic%20depth%20of%20field%2C%20ultra%20realistic%20photography%2C%20no%20text&width=1024&height=600&seq=hero-tablet-v2&orientation=landscape"
          width={1024}
          height={600}
        />
        <source
          media="(min-width: 1024px)"
          type="image/webp"
          srcSet="https://readdy.ai/api/search-image?query=dramatic%20cinematic%20wide%20shot%20of%20a%20rugged%20Pacific%20Northwest%20coastal%20highway%20at%20golden%20hour%2C%20a%20sleek%20dark%20SUV%20parked%20on%20the%20cliff%20edge%20overlooking%20the%20Oregon%20coast%20ocean%2C%20moody%20amber%20and%20deep%20teal%20sky%2C%20misty%20sea%20spray%2C%20cinematic%20depth%20of%20field%2C%20ultra%20realistic%20photography%2C%20no%20text&width=1920&height=1080&seq=hero-desktop-v2&orientation=landscape 1920w"
          sizes="100vw"
          width={1400}
          height={820}
        />
        <img
          src="https://readdy.ai/api/search-image?query=dramatic%20cinematic%20wide%20shot%20of%20a%20rugged%20Pacific%20Northwest%20coastal%20highway%20at%20golden%20hour%2C%20a%20sleek%20dark%20SUV%20parked%20on%20the%20cliff%20edge%20overlooking%20the%20Oregon%20coast%20ocean%2C%20moody%20amber%20and%20deep%20teal%20sky%2C%20misty%20sea%20spray%2C%20cinematic%20depth%20of%20field%2C%20ultra%20realistic%20photography%2C%20no%20text&width=1400&height=820&seq=hero-desktop-v2&orientation=landscape"
          alt="Oregon Coast scenic drive — Rockhound Auto, Warrenton OR"
          className="absolute inset-0 w-full h-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width={1400}
          height={820}
          aria-hidden="true"
        />
      </picture>

      {/* Overlay */}
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(to bottom, rgba(5,10,22,0.88) 0%, rgba(5,10,22,0.78) 45%, rgba(5,10,22,0.96) 100%)' }}
        aria-hidden="true"
      />

      {/* ── MOBILE layout (sm:hidden) ──────────────────────────────────── */}
      <div
        className="sm:hidden relative w-full flex flex-col items-center justify-center text-center"
        style={{
          minHeight: 'clamp(300px, 52vw, 420px)',
          padding: '0 14px 12px',
          paddingTop: 'calc(var(--nav-height) + 10px)',
          zIndex: 10,
        }}
      >
        {/* Eyebrow */}
        <div
          className="inline-flex items-center gap-1.5 mb-1.5 px-2.5 py-0.5 rounded-full"
          style={{ background: 'rgba(245,200,66,0.55)', border: '2px solid #F5C842' }}
        >
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#1A2332' }} aria-hidden="true" />
          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.58rem', letterSpacing: '0.12em', color: '#1A2332', fontWeight: 800, textTransform: 'uppercase' }}>
            Car Broker · Warrenton, OR
          </span>
        </div>

        {/* H1 */}
        <h1
          className="font-black uppercase mb-1 text-center"
          style={{
            fontFamily: "'Oswald', Arial, sans-serif",
            fontSize: 'clamp(1.1rem, 6vw, 1.65rem)',
            lineHeight: 1.08,
            letterSpacing: '-0.01em',
            color: '#FFFFFF',
            textShadow: '0 3px 16px rgba(0,0,0,0.90), 0 1px 4px rgba(0,0,0,0.80)',
            maxWidth: '300px',
            width: '100%',
          }}
        >
          Skip the Dealership.{' '}
          <br />
          <span style={{ color: '#F5C842' }}>Get a Better Deal.</span>
        </h1>

        {/* Accent divider */}
        <div className="flex items-center justify-center gap-2 mb-1.5">
          <div style={{ width: '24px', height: '2px', background: 'rgba(255,255,255,0.28)', borderRadius: '2px' }} />
          <div style={{ width: '5px', height: '5px', background: '#F5C842', borderRadius: '50%', flexShrink: 0 }} />
          <div style={{ width: '24px', height: '2px', background: 'rgba(255,255,255,0.28)', borderRadius: '2px' }} />
        </div>

        {/* Subtext */}
        <p
          className="mb-2.5 text-center"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '0.72rem',
            color: 'rgba(255,255,255,0.92)',
            fontWeight: 600,
            lineHeight: 1.4,
            maxWidth: '260px',
            textShadow: '0 2px 12px rgba(0,0,0,0.95)',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
          }}
        >
          Oregon Coast car broker — we hunt, negotiate, and deliver. One flat fee, zero runaround.
        </p>

        {/* ── Mobile Search Card ── */}
        <div
          className="w-full rounded-xl overflow-visible"
          style={{
            background: 'rgba(253,246,227,0.98)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.50), 0 0 0 2px rgba(245,200,66,0.55)',
            border: '2px solid rgba(245,200,66,0.45)',
            position: 'relative',
            zIndex: 9999,
          }}
        >
          {/* Keyword + Filters row */}
          <div className="flex items-center gap-2 px-2 pt-2 pb-2">
            {/* ── Autocomplete search input ── */}
            <InventorySearchInput
              value={keyword}
              onChange={setKeyword}
              onSearch={handleSearch}
              placeholder="Make, model, keyword…"
              showButton={false}
              size="sm"
              className="flex-1"
              wrapperStyle={{
                background: '#F9FAFB',
                border: '1.5px solid #E5E7EB',
                borderRadius: '7px',
                height: '38px',
              }}
              inputClassName="text-[0.78rem]"
            />

            {/* Filters button */}
            <button
              type="button"
              onClick={() => setMobileFiltersOpen(true)}
              aria-label="Open filters"
              className="relative flex items-center gap-1.5 cursor-pointer whitespace-nowrap flex-shrink-0"
              style={{
                height: '38px',
                padding: '0 10px',
                background: activeFilterCount > 0 ? '#1A2540' : '#F9FAFB',
                border: activeFilterCount > 0 ? '1.5px solid #1A2540' : '1.5px solid #E5E7EB',
                borderRadius: '7px',
                fontFamily: "'Inter', sans-serif",
                fontSize: '0.78rem',
                fontWeight: 700,
                color: activeFilterCount > 0 ? '#F5C842' : '#374151',
              }}
            >
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                <i className="ri-equalizer-line" style={{ fontSize: '0.85rem' }}></i>
              </span>
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span
                  className="absolute -top-1.5 -right-1.5 flex items-center justify-center rounded-full text-white font-bold"
                  style={{ width: '16px', height: '16px', background: '#EF4444', fontSize: '0.6rem' }}
                  aria-label={`${activeFilterCount} filters active`}
                >
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* Active filter chips (shown when filters are set) */}
          {activeFilterCount > 0 && (
            <div className="flex items-center gap-1.5 px-2 pb-1.5">
              {make !== 'Any Make' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(26,37,64,0.08)', color: '#1A2332', border: '1px solid rgba(26,37,64,0.15)' }}>
                  {make}
                  <button type="button" onClick={() => setMake('Any Make')} className="cursor-pointer ml-0.5" aria-label={`Remove ${make} filter`} style={{ color: '#6B7280', fontSize: '0.65rem' }}>✕</button>
                </span>
              )}
              {year !== 'Any Year' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(26,37,64,0.08)', color: '#1A2332', border: '1px solid rgba(26,37,64,0.15)' }}>
                  {year}
                  <button type="button" onClick={() => setYear('Any Year')} className="cursor-pointer ml-0.5" aria-label={`Remove ${year} filter`} style={{ color: '#6B7280', fontSize: '0.65rem' }}>✕</button>
                </span>
              )}
              {maxPrice !== 'Any Price' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(26,37,64,0.08)', color: '#1A2332', border: '1px solid rgba(26,37,64,0.15)' }}>
                  {maxPrice}
                  <button type="button" onClick={() => setMaxPrice('Any Price')} className="cursor-pointer ml-0.5" aria-label={`Remove ${maxPrice} filter`} style={{ color: '#6B7280', fontSize: '0.65rem' }}>✕</button>
                </span>
              )}
            </div>
          )}

          {/* Search button */}
          <div className="px-2 pb-2">
            <button
              type="button"
              onClick={handleSearch}
              className="w-full flex items-center justify-center gap-2 font-bold rounded-lg cursor-pointer transition-all active:scale-95 whitespace-nowrap"
              style={{
                height: '38px',
                background: '#1A2540',
                color: '#F5C842',
                fontFamily: "'Inter', sans-serif",
                fontSize: '0.82rem',
                border: 'none',
                boxShadow: '0 2px 8px rgba(26,37,64,0.30)',
                borderRadius: '7px',
              }}
              aria-label="Search inventory"
            >
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                <i className="ri-search-line"></i>
              </span>
              Search Inventory
            </button>
          </div>
        </div>

        {/* Secondary broker link */}
        <Link
          to="/broker-services"
          className="mt-2 flex items-center justify-center gap-1.5 cursor-pointer transition-opacity hover:opacity-90 whitespace-nowrap px-4 py-1.5 rounded-full"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '0.68rem',
            color: '#1A2332',
            textDecoration: 'none',
            background: 'rgba(245,200,66,0.90)',
            fontWeight: 700,
            border: '1.5px solid rgba(245,200,66,0.70)',
          }}
        >
          <span className="w-3.5 h-3.5 flex items-center justify-center" aria-hidden="true">
            <i className="ri-focus-3-line" style={{ fontSize: '0.68rem' }}></i>
          </span>
          Or let us find your car for you →
        </Link>
      </div>

      {/* ── Mobile Filters Bottom Sheet ── */}
      {mobileFiltersOpen && (
        <>
          {/* Backdrop */}
          <div
            className="sm:hidden fixed inset-0 bg-black/50 z-[99998]"
            onClick={() => setMobileFiltersOpen(false)}
            aria-hidden="true"
          />
          {/* Sheet */}
          <div
            className="sm:hidden fixed bottom-0 left-0 right-0 z-[99999] rounded-t-2xl"
            style={{
              background: '#FFFDF5',
              boxShadow: '0 -8px 40px rgba(0,0,0,0.25)',
              border: '1.5px solid rgba(245,200,66,0.40)',
              borderBottom: 'none',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Filter options"
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div style={{ width: '36px', height: '4px', background: '#D1D5DB', borderRadius: '2px' }} />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b" style={{ borderColor: '#F3F4F6' }}>
              <span style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1rem', fontWeight: 700, color: '#1A2332', letterSpacing: '0.02em' }}>
                FILTER VEHICLES
              </span>
              <div className="flex items-center gap-3">
                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="cursor-pointer"
                    style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.75rem', color: '#EF4444', fontWeight: 600 }}
                  >
                    Clear all
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="cursor-pointer w-7 h-7 flex items-center justify-center rounded-full"
                  style={{ background: '#F3F4F6', color: '#6B7280' }}
                  aria-label="Close filters"
                >
                  <i className="ri-close-line" style={{ fontSize: '0.9rem' }}></i>
                </button>
              </div>
            </div>

            {/* Filter sections */}
            <div className="px-4 py-3 space-y-4">
              {/* Make */}
              <div>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>Make</p>
                <div className="flex flex-wrap gap-2">
                  {MAKES.map(m => {
                    const count = m === 'Any Make' ? vehicles.length : (makeCounts[m] ?? 0);
                    const isSelected = make === m;
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setMake(m)}
                        className="cursor-pointer px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5"
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          background: isSelected ? '#1A2540' : '#F3F4F6',
                          color: isSelected ? '#F5C842' : '#374151',
                          border: isSelected ? '1.5px solid #1A2540' : '1.5px solid transparent',
                        }}
                      >
                        <span>{m}</span>
                        <span
                          className="inline-flex items-center justify-center rounded-full font-bold leading-none"
                          style={{
                            minWidth: '16px',
                            height: '16px',
                            padding: '0 4px',
                            fontSize: '0.6rem',
                            background: isSelected ? 'rgba(245,200,66,0.25)' : 'rgba(26,35,50,0.10)',
                            color: isSelected ? '#F5C842' : '#6B7280',
                          }}
                        >
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Year */}
              <div>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>Year</p>
                <div className="flex flex-wrap gap-2">
                  {YEARS.map(y => (
                    <button
                      key={y}
                      type="button"
                      onClick={() => setYear(y)}
                      className="cursor-pointer px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        background: year === y ? '#1A2540' : '#F3F4F6',
                        color: year === y ? '#F5C842' : '#374151',
                        border: year === y ? '1.5px solid #1A2540' : '1.5px solid transparent',
                      }}
                    >
                      {y}
                    </button>
                  ))}
                </div>
              </div>

              {/* Max Price */}
              <div>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>Max Price</p>
                <div className="flex flex-wrap gap-2">
                  {MAX_PRICES.map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setMaxPrice(p)}
                      className="cursor-pointer px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        background: maxPrice === p ? '#1A2540' : '#F3F4F6',
                        color: maxPrice === p ? '#F5C842' : '#374151',
                        border: maxPrice === p ? '1.5px solid #1A2540' : '1.5px solid transparent',
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Apply button */}
            <div className="px-4 pb-4 pt-2 border-t" style={{ borderColor: '#F3F4F6' }}>
              {/* Live match counter */}
              <div className="flex items-center justify-center gap-2 mb-3 py-2 rounded-xl" style={{ background: previewMatchCount > 0 ? 'rgba(26,37,64,0.05)' : 'rgba(239,68,68,0.06)' }}>
                <span
                  className="inline-flex items-center justify-center rounded-full font-black"
                  style={{
                    minWidth: '28px',
                    height: '28px',
                    padding: '0 6px',
                    fontSize: '0.85rem',
                    background: previewMatchCount > 0 ? '#1A2540' : '#EF4444',
                    color: previewMatchCount > 0 ? '#F5C842' : '#ffffff',
                  }}
                >
                  {previewMatchCount}
                </span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.78rem', fontWeight: 600, color: previewMatchCount > 0 ? '#374151' : '#EF4444' }}>
                  {previewMatchCount === 0
                    ? 'No vehicles match — try loosening filters'
                    : previewMatchCount === 1
                    ? 'vehicle matches your filters'
                    : 'vehicles match your filters'}
                </span>
              </div>

              <button
                type="button"
                onClick={handleSearch}
                disabled={previewMatchCount === 0}
                className="w-full flex items-center justify-center gap-2 font-bold rounded-xl cursor-pointer transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  height: '48px',
                  background: previewMatchCount > 0 ? '#1A2540' : '#94A3B8',
                  color: previewMatchCount > 0 ? '#F5C842' : '#ffffff',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '0.9rem',
                  border: 'none',
                  boxShadow: previewMatchCount > 0 ? '0 4px 16px rgba(26,37,64,0.30)' : 'none',
                }}
              >
                <span className="w-4 h-4 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <i className="ri-search-line"></i>
                </span>
                {previewMatchCount === 0
                  ? 'No matches'
                  : `See ${previewMatchCount} Vehicle${previewMatchCount !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── DESKTOP layout (hidden sm:flex) ──────────────────────────────── */}
      <div
        className="hidden sm:flex relative w-full flex-col items-center justify-center text-center"
        style={{
          minHeight: 'clamp(420px, 55vh, 600px)',
          maxWidth: '1400px',
          margin: '0 auto',
          padding: 'clamp(12px, 3vw, 40px) clamp(16px, 5vw, 80px) clamp(16px, 3vw, 40px)',
          paddingTop: 'calc(var(--nav-height) + clamp(12px, 2vw, 28px))',
          overflow: 'visible',
          zIndex: 10,
        }}
      >
        {/* Eyebrow */}
        <div
          className="inline-flex items-center gap-2 mb-3 px-4 py-1.5 rounded-full"
          style={{ background: 'rgba(245,200,66,0.55)', border: '2px solid #F5C842' }}
        >
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#1A2332' }} aria-hidden="true" />
          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.65rem', letterSpacing: '0.12em', color: '#1A2332', fontWeight: 800, textTransform: 'uppercase' }}>
            Car Broker · Warrenton, Oregon
          </span>
        </div>

        {/* Desktop H1 headline */}
        <h1
          className="font-black uppercase mb-2 text-center"
          style={{
            fontFamily: "'Oswald', Arial, sans-serif",
            fontSize: 'clamp(1.35rem, 4.5vw, 3.2rem)',
            lineHeight: 1.08,
            letterSpacing: '-0.01em',
            color: '#FFFFFF',
            maxWidth: '860px',
            width: '100%',
            textShadow: '0 3px 16px rgba(0,0,0,0.90), 0 1px 4px rgba(0,0,0,0.80)',
          }}
        >
          Stop Overpaying at Dealerships.{' '}
          <br />
          <span style={{ color: '#F5C842', textShadow: '0 2px 12px rgba(0,0,0,0.80)' }}>
            We Hunt. We Negotiate. You Win.
          </span>
        </h1>

        {/* Divider accent */}
        <div className="flex items-center justify-center gap-3 mb-3">
          <div style={{ width: '36px', height: '2px', background: 'rgba(255,255,255,0.30)', borderRadius: '2px' }} />
          <div style={{ width: '7px', height: '7px', background: '#F5C842', borderRadius: '50%', flexShrink: 0 }} />
          <div style={{ width: '36px', height: '2px', background: 'rgba(255,255,255,0.30)', borderRadius: '2px' }} />
        </div>

        {/* Subtext */}
        <p className="mb-5 text-center px-2" style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 'clamp(0.82rem, 1.8vw, 1rem)',
          color: '#FFFFFF',
          fontWeight: 700,
          lineHeight: 1.5,
          maxWidth: '560px',
          textShadow: '0 2px 12px rgba(0,0,0,0.95), 0 1px 6px rgba(0,0,0,0.90)',
        }}>
          Oregon Coast&apos;s trusted car broker — real prices, straight answers, and avg. $2,400 saved per client. No scripts. No pressure. Just results.
        </p>

        {/* Search Card */}
        <div className="w-full flex flex-col items-center" style={{ maxWidth: '780px', animation: 'heroFadeUp 0.55s ease both', overflow: 'visible', position: 'relative', zIndex: 9999 }}>
          {/* Tab switcher */}
          <div className="flex w-full" role="tablist" aria-label="Search options">
            {[
              { key: 'search', label: 'Search Inventory', icon: 'ri-search-line' },
              { key: 'broker', label: 'Find My Car For Me', icon: 'ri-focus-3-line' },
            ].map(tab => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                id={`hero-tab-${tab.key}`}
                aria-selected={activeTab === tab.key}
                aria-controls={`hero-panel-${tab.key}`}
                onClick={() => setActiveTab(tab.key as 'search' | 'broker')}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 cursor-pointer transition-all whitespace-nowrap"
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 'clamp(0.7rem, 1.5vw, 0.8rem)',
                  fontWeight: 700,
                  borderRadius: '8px 8px 0 0',
                  background: activeTab === tab.key ? '#ffffff' : 'rgba(255,255,255,0.92)',
                  color: activeTab === tab.key ? '#1A2332' : '#4B5563',
                  border: activeTab === tab.key ? 'none' : '1.5px solid rgba(255,255,255,0.60)',
                  borderBottom: 'none',
                  outline: 'none',
                  opacity: activeTab === tab.key ? 1 : 0.85,
                }}
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <i className={tab.icon} style={{ fontSize: '0.75rem' }}></i>
                </span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Search panel */}
          {activeTab === 'search' ? (
            <div
              id="hero-panel-search"
              role="tabpanel"
              aria-labelledby="hero-tab-search"
              className="w-full"
              style={{
                background: 'rgba(253,246,227,0.98)',
                borderRadius: '0 0 10px 10px',
                boxShadow: '0 16px 64px rgba(0,0,0,0.60), 0 0 0 2px rgba(245,200,66,0.55)',
                border: '2px solid rgba(245,200,66,0.45)',
                borderTop: 'none',
              }}
            >
              <div className="flex items-center px-3 py-3 gap-0" style={{ overflow: 'visible', position: 'relative', zIndex: 9999 }}>
                {/* ── Autocomplete search input (desktop) ── */}
                <InventorySearchInput
                  value={keyword}
                  onChange={setKeyword}
                  onSearch={handleSearch}
                  placeholder="Make, model, keyword…"
                  showButton={false}
                  size="sm"
                  className="flex-shrink-0 w-[210px]"
                  wrapperStyle={{
                    background: '#F9FAFB',
                    border: '1.5px solid #E5E7EB',
                    borderRadius: '6px',
                    height: '44px',
                  }}
                />
                <div style={{ width: '1px', background: '#E5E7EB', margin: '6px 0', flexShrink: 0 }} aria-hidden="true" />
                <SelectDropdown label="Make" value={make} options={MAKES} onChange={setMake} icon="ri-car-line" />
                <div style={{ width: '1px', background: '#E5E7EB', margin: '6px 0', flexShrink: 0 }} aria-hidden="true" />
                <SelectDropdown label="Year" value={year} options={YEARS} onChange={setYear} icon="ri-calendar-line" />
                <div style={{ width: '1px', background: '#E5E7EB', margin: '6px 0', flexShrink: 0 }} aria-hidden="true" />
                <SelectDropdown label="Max Price" value={maxPrice} options={MAX_PRICES} onChange={setMaxPrice} icon="ri-price-tag-3-line" />
                <div style={{ width: '1px', background: '#E5E7EB', margin: '6px 0', flexShrink: 0 }} aria-hidden="true" />
                <button
                  type="button"
                  onClick={handleSearch}
                  className="flex items-center justify-center gap-2 font-bold rounded-lg cursor-pointer transition-all hover:opacity-90 active:scale-95 whitespace-nowrap ml-3 flex-shrink-0"
                  style={{
                    padding: '0 24px',
                    height: '44px',
                    background: '#1A2540',
                    color: '#F5C842',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '0.85rem',
                    border: 'none',
                    boxShadow: '0 2px 8px rgba(26,37,64,0.30)',
                    borderRadius: '6px',
                  }}
                  aria-label="Search inventory"
                >
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                    <i className="ri-search-line"></i>
                  </span>
                  <span>Search</span>
                </button>
              </div>
            </div>
          ) : (
            <div
              id="hero-panel-broker"
              role="tabpanel"
              aria-labelledby="hero-tab-broker"
              className="w-full flex flex-col sm:flex-row items-center gap-4 p-4"
              style={{
                background: 'rgba(253,246,227,0.98)',
                borderRadius: '0 0 10px 10px',
                boxShadow: '0 16px 64px rgba(0,0,0,0.60), 0 0 0 2px rgba(245,200,66,0.55)',
                border: '2px solid rgba(245,200,66,0.45)',
                borderTop: 'none',
              }}
            >
              <div className="flex-1 w-full">
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.875rem', color: '#374151', lineHeight: 1.6 }}>
                  <strong style={{ color: '#1A2332' }}>Drop us your wish list.</strong> We&apos;ll hunt it down, go to bat on price, and deliver it straight to your door — one flat fee, fully transparent.
                </p>
                <div className="flex flex-wrap gap-3 mt-2">
                  {['Avg. $2,400 saved', 'We handle the haggling', 'One flat fee', 'Oregon Coast delivery'].map(item => (
                    <span key={item} className="flex items-center gap-1" style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.75rem', color: '#6B7280' }}>
                      <span aria-hidden="true" style={{ color: '#22C55E', fontSize: '0.85rem' }}>✓</span>
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <Link
                to="/broker-services"
                className="flex items-center justify-center gap-2 font-bold rounded-lg cursor-pointer transition-all hover:opacity-90 whitespace-nowrap flex-shrink-0 w-full sm:w-auto"
                style={{
                  padding: '12px 24px',
                  background: '#1A2540',
                  color: '#F5C842',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '0.9rem',
                  textDecoration: 'none',
                  boxShadow: '0 2px 8px rgba(26,37,64,0.30)',
                  borderRadius: '6px',
                }}
              >
                <span className="w-4 h-4 flex items-center justify-center" aria-hidden="true">
                  <i className="ri-focus-3-line"></i>
                </span>
                Unleash the Hunt
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Trust Indicators */}
      <div
        className="relative w-full"
        style={{
          background: 'rgba(15,22,38,0.72)',
          backdropFilter: 'blur(6px)',
          borderTop: '1px solid rgba(255,255,255,0.10)',
        }}
      >
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-4 sm:gap-10 px-4 py-1.5 sm:py-2.5">
          {TRUST_ITEMS.map(item => (
            <div
              key={item.label}
              className={`flex items-center gap-1.5 ${item.priority > 2 ? 'hidden sm:flex' : ''}`}
            >
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                <i className={item.icon} style={{ fontSize: '0.875rem', color: '#F5C842' }}></i>
              </span>
              <div className="flex flex-col leading-tight">
                <span className="font-semibold whitespace-nowrap" style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.7rem', color: '#FFFFFF' }}>
                  <span className="hidden sm:inline">{item.label}</span>
                  <span className="sm:hidden">{item.mobileLabel}</span>
                </span>
                <span className="whitespace-nowrap" style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.6rem', color: 'rgba(255,255,255,0.70)' }}>
                  {item.sub}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
